import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import {
  MobileGameHolder,
  MobileGameStructure,
  MobileGameWr,
} from '@pascal/pascal-core/table-line/table-components';
import {
  DefaultQueryParams,
  parseQuery,
  RightSideHolder,
  useTranslation,
  useUserCountStore,
  MobileStructureMiddle,
  tableBetHistoryStore,
  GameControllers,
  useUserInfoStore,
  isMobile,
  convertAmount,
  iframeService,
  platformMessageStore,
  useSoundWatcher, TableMobileHeader,
} from '@pascal/pascal-core';
import {
  freeBetStore,
  userInfoStore,
  gameConfigStore,
  applicationStore,
} from '@pascal/pascal-core/shared/core/store';
import { GAME_CONTROLLER_TYPES } from '@pascal/pascal-core/shared/core/enums/gameControllerTypes';
import { GameControllerTypes } from '@pascal/pascal-core/table-line/t_types/enums/GameControllerTypes';

import {
  AdditionalResponseCode,
  AdditionalErrorMessages,
  AdditionalNotificationByResponseCode,
} from '../enums/notifications';
import { MobileGameMain } from '../game';
import { BetHistoryWr } from './betHistory';
import LiveBets from '../components/liveBets';
import { autoBetStore } from '../store/autoBetStore';
import { HelpPopup } from '../components/helpPopup';
import { SettingsContainer } from './settingsContainer';
import { TranslationKeys } from '../enums/translationKeys';
import { GAME_CONTROLLER_TYPES_YET, maxOddValue, MIN_ODD } from '../enums/constants';
import logo from '../assets/images/logo.svg';

const gameControllerComponents = {
  [GAME_CONTROLLER_TYPES_YET.BETS]: <LiveBets />,
  [GameControllerTypes.Help]: <HelpPopup />,
  [GameControllerTypes.History]: <BetHistoryWr />,
  [GameControllerTypes.Settings]: <SettingsContainer />,
};

// const secondNumber = 1;

export const MobileGame = observer(() => {
  useSoundWatcher();
  const t = useTranslation();
  const { search } = useLocation();
  const { usersCount } = useUserCountStore();
  const { isDemo } = applicationStore;
  const { activeFreeBet } = freeBetStore;
  const { resetAutoBet } = autoBetStore;
  const { currencySymbolOrCode } = userInfoStore;
  const { currencyCode } = useUserInfoStore();
  const {
    limits: { minBet, maxBet },
  } = gameConfigStore;
  const { [DefaultQueryParams.UserId]: userId } = parseQuery(search);
  const [gameControllerType, setGameControllerType] = useState('');

  const { isOpenBetHistorySection, setIsOpenBetHistorySection } = platformMessageStore;
  const { selectedHistoryRow, setSelectedHistoryRow } = tableBetHistoryStore;

  const GameControllerComponent = useMemo(() => {
    const component = gameControllerComponents[gameControllerType];
    if (component) {
      return component;
    }
    return null;
  }, [gameControllerType]);

  useEffect(() => {
    if (!gameControllerType) {
      setIsOpenBetHistorySection(false);
    }
    if (isOpenBetHistorySection) {
      setGameControllerType(GAME_CONTROLLER_TYPES.HISTORY);
    }
  }, [isOpenBetHistorySection, gameControllerType]);

  const handleResetAutoBet = useCallback(() => {
    resetAutoBet();
  }, [resetAutoBet]);

  useEffect(() => {
    if (isMobile()) {
      document.addEventListener('visibilitychange', handleResetAutoBet);
    }
    return () => {
      if (isMobile()) {
        document.removeEventListener('visibilitychange', handleResetAutoBet);
      }
    };
  }, [handleResetAutoBet]);

  const navigationItems = useMemo(() => {
    const items = [
      {
        icon: 'icon-bets',
        name: t(TranslationKeys.Bets),
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES_YET.BETS),
      },
      {
        icon: 'icon-history',
        name: t(TranslationKeys.History),
        isDisabled: !userId || isDemo,
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES.HISTORY),
      },
      {
        icon: 'icon-help',
        name: t(TranslationKeys.Help),
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES.HELP),
      },
      {
        icon: 'icon-settings',
        name: t(TranslationKeys.Settings),
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES.SETTINGS),
      },
    ];
    return items;
  }, [isDemo, t, userId]);

  useEffect(() => {
    const el = document.createElement('span');
    const parent = document.querySelector('.m-user-count-holder');
    el.classList.add('m-user-count-txt');
    el.innerText = usersCount || '';
    parent?.prepend(el);
  }, [usersCount]);

  return (
    <MobileGameWr>
      <MobileGameStructure hasLobby={false}>
        <TableMobileHeader
          isShowFairness={false}
          isShowGameTypes={false}
          isShowMinMax={false}
          logoSrc={logo}
        />
        <MobileStructureMiddle
          haveNotification
          AdditionalResponseCode={AdditionalResponseCode}
          AdditionalErrorMessages={AdditionalErrorMessages}
          AdditionalNotificationByResponseCode={AdditionalNotificationByResponseCode}
          translationValues={{
            minBet: `${minBet} ${currencySymbolOrCode}`,
            maxBet,
            minOdd: `x${activeFreeBet.id ? activeFreeBet.minOdd : MIN_ODD}`,
            maxOdd: `x${convertAmount(maxOddValue)}`,
          }}
        >
          <MobileGameHolder>
            <MobileGameMain />
          </MobileGameHolder>
          <GameControllers
            currencyCode={currencyCode}
            controllerType={gameControllerType}
            selectedHistoryRow={selectedHistoryRow}
            setSelectedHistoryRow={setSelectedHistoryRow}
            gameControllerComponent={GameControllerComponent}
            setGameControllerType={() => setGameControllerType(null)}
          />
          <RightSideHolder navigationItems={navigationItems} hasLobby={false} />
        </MobileStructureMiddle>
      </MobileGameStructure>
    </MobileGameWr>
  );
});
